// Pure helpers + validation for the constraint-driven will intake engine.
// All functions return { ok: boolean, error?: string } or plain derived values.

export const RELIGIONS = ["Hindu", "Christian", "Sikh", "Jain", "Buddhist", "Parsi", "Muslim", "Other"];
export const MARITAL = ["Single", "Married", "Divorced", "Widowed"];
export const ASSET_TYPES = ["Property", "Bank Account", "Investments", "Jewellery / Personal Assets", "Insurance"];
export const RELATIONSHIPS = [
  "Spouse",
  "Father",
  "Mother",
  "Son",
  "Daughter",
  "Brother",
  "Sister",
  "Other",
];

export const DIGITAL_ASSET_OPTIONS = [
  "Email accounts",
  "Social media accounts",
  "Trading / brokerage accounts",
  "Crypto wallets",
  "Websites / domain names",
  "Cloud storage",
];

export const FUNERAL_OPTIONS = [
  "Cremation",
  "Burial",
  "Religious/custom",
  "Custom instruction",
];

export function uid() {
  return (
    Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)
  );
}

export function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

export function hasMinors(family) {
  return family.some((m) => Number(m.age) < 18);
}

// ---------- STEP VALIDATORS ----------

// Auto-capitalise: trim, collapse whitespace, capitalise each word.
export function capitalizeName(raw) {
  if (!raw) return raw;
  return String(raw)
    .replace(/\s+/g, " ")
    .replace(/^\s+|\s+$/g, "")
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// Reject purely numeric / single-character / junk names
export function isReasonableName(value) {
  if (!value) return false;
  const v = String(value).trim();
  if (v.length < 2) return false;
  if (/^\d+$/.test(v)) return false;
  if (!/[A-Za-z\u00C0-\u017F\u0900-\u097F]/.test(v)) return false; // must contain a letter (incl. Devanagari)
  return true;
}

export function validatePerson(p, label = "You") {
  if (!p.full_name?.trim()) return `${label}: full legal name is required.`;
  if (!isReasonableName(p.full_name))
    return `${label}: please enter a valid full name (letters, not numbers).`;
  if (!p.date_of_birth?.trim()) return `${label}: date of birth is required.`;
  const age = ageFromDob(p.date_of_birth);
  if (age === null) return `${label}: please enter a valid date of birth.`;
  if (age < 18) return `${label}: testator must be 18 years or older.`;
  if (!p.address?.trim()) return `${label}: residential address is required.`;
  if (p.address.trim().length < 6)
    return `${label}: please enter your complete residential address.`;
  if (!p.religion) return `${label}: please select religion.`;
  if (p.religion === "Muslim") {
    return `${label}: Muslim inheritance laws involve specialised rules — please request a Lawyer Review to proceed.`;
  }
  if (!p.marital_status) return `${label}: please select marital status.`;
  if (!p.sound_mind) return `${label}: please confirm sound-mind declaration to proceed.`;
  return null;
}

export function validateFamily(family) {
  if (!Array.isArray(family) || family.length === 0)
    return "Please add at least one family member.";
  for (let i = 0; i < family.length; i++) {
    const m = family[i];
    if (!m.name?.trim()) return `Family member #${i + 1}: name is required.`;
    if (!isReasonableName(m.name))
      return `Family member #${i + 1}: name looks invalid (use letters, not just numbers).`;
    if (!m.relationship) return `Family member #${i + 1}: relationship is required.`;
    const age = Number(m.age);
    if (!Number.isFinite(age) || age < 0 || age > 120)
      return `Family member #${i + 1}: please enter a valid age (0–120).`;
  }
  return null;
}

export function validateAssets(assets) {
  if (!Array.isArray(assets) || assets.length === 0)
    return "Please add at least one asset.";
  for (let i = 0; i < assets.length; i++) {
    const a = assets[i];
    if (!a.type) return `Asset #${i + 1}: please choose a type.`;
    if (!a.description?.trim())
      return `Asset #${i + 1}: description is required.`;
  }
  return null;
}

export function percentageTotal(percentages) {
  return percentages.reduce((s, p) => s + (Number(p.share) || 0), 0);
}

export function validateDistribution(distribution, assets) {
  if (!distribution?.mode)
    return "Please choose how you want to distribute your estate.";
  if (distribution.mode === "percentage") {
    const list = distribution.percentages || [];
    if (list.length === 0) return "Add at least one beneficiary and their percentage share.";
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      if (!p.beneficiary_name?.trim())
        return `Beneficiary #${i + 1}: name is required.`;
      const share = Number(p.share);
      if (!Number.isFinite(share) || share <= 0 || share > 100)
        return `Beneficiary #${i + 1}: share must be a number between 0 and 100.`;
      if (p.is_minor) {
        if (!p.guardian_name?.trim())
          return `Beneficiary #${i + 1} is marked as a minor — guardian name is required.`;
        if (!p.guardian_relation?.trim())
          return `Beneficiary #${i + 1} is marked as a minor — guardian relation is required.`;
      }
    }
    const total = percentageTotal(list);
    if (Math.abs(total - 100) > 0.001) {
      if (total < 100)
        return `Shares must add up to 100%. Currently at ${total}%.`;
      return `Shares must add up to 100%. Currently at ${total}% (over-allocated).`;
    }
    return null;
  }
  if (distribution.mode === "asset_wise") {
    const byId = new Map();
    (distribution.asset_assignments || []).forEach((a) => byId.set(a.asset_id, a));
    const unassigned = assets
      .filter((a) => {
        const row = byId.get(a.id);
        return !row || !row.beneficiaries || row.beneficiaries.length === 0;
      })
      .map((a) => a.description || a.type);
    if (unassigned.length > 0) {
      return `These assets are unassigned: ${unassigned.join(", ")}. Every asset must go to at least one beneficiary.`;
    }
    return null;
  }
  return "Unknown distribution mode.";
}

export function validateExecutor(executor) {
  if (!executor.name?.trim()) return "Executor name is required.";
  if (!isReasonableName(executor.name))
    return "Executor name looks invalid (use letters, not just numbers).";
  if (!executor.relationship?.trim())
    return "Executor relationship is required.";
  if (!executor.phone?.trim()) return "Executor phone number is required.";
  return null;
}

export function validateGuardian(guardian, required) {
  if (!required) return null;
  if (!guardian?.name?.trim()) return "Guardian name is required (minor children present).";
  if (!isReasonableName(guardian.name))
    return "Guardian name looks invalid (use letters, not just numbers).";
  if (!guardian?.relationship?.trim())
    return "Guardian relationship is required.";
  return null;
}

// ---------- PROGRESS / CHECKLIST ----------

export function buildChecklist({
  mode,
  person,
  person2,
  family,
  assets,
  distribution,
  executor,
  guardian,
  minorsExist,
}) {
  const checks = [
    {
      id: "person",
      label: mode === "joint" ? "Testator 1 details complete" : "Personal details complete",
      ok: !validatePerson(person, "Testator"),
    },
  ];
  if (mode === "joint") {
    checks.push({
      id: "person2",
      label: "Testator 2 details complete",
      ok: !validatePerson(person2, "Testator 2"),
    });
  }
  checks.push(
    { id: "family", label: "At least one family member added", ok: !validateFamily(family) },
    { id: "assets", label: "At least one asset listed", ok: !validateAssets(assets) },
    {
      id: "distribution",
      label:
        distribution?.mode === "percentage"
          ? "Percentage shares total exactly 100%"
          : distribution?.mode === "asset_wise"
          ? "Every asset assigned to a beneficiary"
          : "Distribution method chosen",
      ok: !validateDistribution(distribution, assets),
    },
    { id: "executor", label: "Executor appointed", ok: !validateExecutor(executor) },
    {
      id: "guardian",
      label: minorsExist
        ? "Guardian appointed for minor children"
        : "No minor children (guardian not required)",
      ok: minorsExist ? !validateGuardian(guardian, true) : true,
    }
  );
  return checks;
}

export function completionPercent(checks) {
  if (!checks.length) return 0;
  const done = checks.filter((c) => c.ok).length;
  return Math.round((done / checks.length) * 100);
}
